import type { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";
import { waitForMinimumDelay } from "@/lib/min-delay";
import { hasUnsavedChanges as hasConnectorFlowUnsavedChanges } from "@/lib/connector-flow-state";
import { useNavigationStore } from "@/store/navigation/navigation-store";
import { hasRegisteredUnsavedChanges } from "@/store/navigation/unsaved-changes-store";

const DEFAULT_UNSAVED_CONFIRM_MESSAGES = {
  en: "You have unsaved changes. Are you sure you want to leave this page?",
  vi: "Bạn có thay đổi chưa lưu. Bạn có chắc chắn muốn rời khỏi trang này không?",
  ar: "لديك تغييرات غير محفوظة. هل أنت متأكد أنك تريد مغادرة هذه الصفحة؟",
} as const;

export function getDefaultUnsavedConfirmMessage(): string {
  const lang = document.documentElement.lang?.toLowerCase();
  if (lang.startsWith("vi")) return DEFAULT_UNSAVED_CONFIRM_MESSAGES.vi;
  if (lang.startsWith("ar")) return DEFAULT_UNSAVED_CONFIRM_MESSAGES.ar;
  return DEFAULT_UNSAVED_CONFIRM_MESSAGES.en;
}

interface NavigateOptions {
  message?: string;
  skipUnsavedCheck?: boolean;
  showLoadingLayer?: boolean;
}

function getCurrentPathname(): string {
  if (typeof window === "undefined") return "";
  return window.location.pathname;
}

function isConnectorFlowPath(pathname: string): boolean {
  return /\/connectors\/(new|add-api|authentication)(\/|$)/.test(pathname);
}

export function hasAnyUnsavedChanges(pathname = getCurrentPathname()): boolean {
  if (hasRegisteredUnsavedChanges()) {
    return true;
  }

  if (!isConnectorFlowPath(pathname)) {
    return false;
  }

  return hasConnectorFlowUnsavedChanges();
}

async function navigate(
  router: AppRouterInstance,
  href: string,
  method: "push" | "replace",
  options: NavigateOptions = {},
): Promise<boolean> {
  if (!options.skipUnsavedCheck && hasAnyUnsavedChanges()) {
    const shouldContinue = window.confirm(
      options.message || getDefaultUnsavedConfirmMessage(),
    );
    if (!shouldContinue) {
      return false;
    }
  }

  const startedAt = Date.now();
  if (options.showLoadingLayer !== false) {
    useNavigationStore.getState().startNavigation(href);
  }
  await waitForMinimumDelay(startedAt);

  if (method === "replace") {
    router.replace(href);
  } else {
    router.push(href);
  }

  return true;
}

export function pushWithMinimumDelay(
  router: AppRouterInstance,
  href: string,
  options?: NavigateOptions,
): Promise<boolean> {
  return navigate(router, href, "push", options);
}

export function replaceWithMinimumDelay(
  router: AppRouterInstance,
  href: string,
  options?: NavigateOptions,
): Promise<boolean> {
  return navigate(router, href, "replace", options);
}

export async function backWithMinimumDelay(
  router: AppRouterInstance,
  fallbackHref: string,
  options?: NavigateOptions,
): Promise<boolean> {
  if (!options?.skipUnsavedCheck && hasAnyUnsavedChanges()) {
    const shouldContinue = window.confirm(
      options?.message || getDefaultUnsavedConfirmMessage(),
    );
    if (!shouldContinue) {
      return false;
    }
  }

  const startedAt = Date.now();
  useNavigationStore.getState().startNavigation("back");
  await waitForMinimumDelay(startedAt);

  if (window.history.length > 1) {
    router.back();
  } else {
    router.push(fallbackHref);
  }

  return true;
}
